import { useState, useDeferredValue } from "react";

import { IconButton, Combobox } from "@similarweb/ui-components";

import type { IKeywordGroup } from "app/pages/digital-marketing/common/hooks/useKeywordGroups";
import { useKeywordGroups } from "app/pages/digital-marketing/common/hooks/useKeywordGroups";
import { useTranslation } from "pro-features/components/WithTranslation";

import { KeywordGroupList } from "./KeywordGroupList";

interface IKeywordsGroupImportComboboxProps {
    addedIds: Set<string>;
    onApply?: (groups: IKeywordGroup[]) => void;
    onOpen?: () => void;
    disabledTooltipContent?: string;
}

export const KeywordsGroupImportCombobox = ({
    addedIds,
    onApply,
    onOpen,
    disabledTooltipContent,
}: IKeywordsGroupImportComboboxProps) => {
    const translate = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    const { userGroups, sharedGroups, isLoading } = useKeywordGroups();

    const deferredSearchValue = useDeferredValue(searchValue);

    const toggleSelection = (groupId: string) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const handleApply = () => {
        const selectedGroups = [...userGroups, ...sharedGroups].filter((group) => selectedItems.has(group.Id));
        onApply?.(selectedGroups);
        setIsOpen(false);
        setSelectedItems(new Set());
        setSearchValue("");
    };

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            placement="top-end"
        >
            <Combobox.Trigger>
                <IconButton
                    type="flat"
                    data-automation="keywords-group-import-trigger"
                    onClick={() => {
                        onOpen?.();
                        setIsOpen(true);
                    }}
                    iconName="keyword-group"
                >
                    {translate("monitor_and_protect.campaign_wizard.keywords.import_from_list")}
                </IconButton>
            </Combobox.Trigger>

            <Combobox.SearchInput
                value={searchValue}
                isLoading={isLoading}
                onChange={setSearchValue}
                placeholder={translate("monitor_and_protect.campaign_wizard.keywords.search_placeholder")}
            />

            <Combobox.Content
                emptyState={
                    isLoading
                        ? translate("monitor_and_protect.keywords_group_import.loading")
                        : deferredSearchValue.trim()
                          ? translate("monitor_and_protect.keywords_group_import.no_results")
                          : translate("monitor_and_protect.keywords_group_import.no_data")
                }
            >
                <KeywordGroupList
                    disabledIds={addedIds}
                    deferredSearchValue={deferredSearchValue}
                    userGroups={userGroups}
                    sharedGroups={sharedGroups}
                    selectedItems={selectedItems}
                    onToggle={toggleSelection}
                    disabledTooltipContent={disabledTooltipContent}
                />
            </Combobox.Content>

            {selectedItems.size === 0 ? null : (
                <Combobox.ApplyButton
                    data-automation="keywords-group-import-apply"
                    onClick={handleApply}
                >
                    {translate("monitor_and_protect.keywords_group_import.apply")}
                </Combobox.ApplyButton>
            )}
        </Combobox>
    );
};
